import * as Icons from '@ant-design/icons';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Flex,
  Form,
  Select,
  Table,
  Tag,
  Tooltip,
  Upload
} from 'antd';
import { AuditOutlined, DeleteOutlined, RollbackOutlined } from '@ant-design/icons';
import { CertificateRow } from 'interfaces/bulk_certificates';
import { CreateLayout } from 'components/layout/layout/CreateLayout';
import { ICertificateKey } from 'interfaces/courses';
import { Link } from 'react-router-dom';
import { PageHeader } from 'components/professor_area/PageHeader';
import { parseFile } from 'services/bulk-certificates';
import { useForm, useSelect } from '@refinedev/antd';
import { useNavigation } from '@refinedev/core';
import { useState } from 'react';
import { getFullNameValidationError, isValidFullName } from 'utils/validators';

interface FileTableProps {
  certificateList: CertificateRow[];
  removedIndices: Set<number>;
  onRemove: (index: number) => void;
  onRestore: (index: number) => void;
}

const FileTable = ({ certificateList, removedIndices, onRemove, onRestore }: FileTableProps) => (
  <Table
    dataSource={certificateList.map((item, index) => ({ ...item, index }))}
    rowKey={(record) => record.index}
    size="small"
    columns={[
      {
        title: 'Email',
        key: 'email',
        dataIndex: 'email',
        render: (text: string, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.email);
        }
      },
      {
        title: 'Nome Completo',
        key: 'recipient_name',
        dataIndex: 'recipient_name',
        render: (text: string, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.recipient_name);
        }
      },
      {
        title: 'Curso',
        key: 'course_name',
        dataIndex: 'course_name',
        render: (text: string, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.course_name);
        }
      },
      {
        title: 'Duração (horas)',
        key: 'duration_in_hours',
        dataIndex: 'duration_in_hours',
        render: (text: string, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.duration_in_hours);
        }
      },
      {
        title: 'Data de conclusão',
        key: 'completed_at',
        dataIndex: 'completed_at',
        render: (text: string, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);
          const errors = getRowErrors(record);
          return renderCell(text, isRemoved || rowInvalid, errors.completed_at);
        }
      },
      {
        title: 'Status',
        key: 'status',
        width: 110,
        align: 'center',
        render: (_: unknown, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);

          if (isRemoved)
            return (
              <Icons.CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
            );
          if (rowInvalid) return <Tag color="warning">Inválido</Tag>;
          return <Icons.CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
        }
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 140,
        render: (_: unknown, record: CertificateRow & { index: number }) => {
          const isRemoved = removedIndices.has(record.index);
          const rowInvalid = !isRowValid(record);

          if (rowInvalid) return null;

          return isRemoved ? (
            <Button
              type="link"
              size="small"
              icon={<RollbackOutlined />}
              onClick={() => onRestore(record.index)}
            >
              Voltar
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onRemove(record.index)}
            >
              Excluir
            </Button>
          );
        }
      }
    ]}
    pagination={{ pageSize: 20 }}
  />
);

const checkField = (field: string) => field?.length && field.length > 0;

const isValidDate = (date: string) =>
  checkField(date) && /^\d{2}\/\d{2}\/\d{4}$/.test(date.trim());

const isValidInteger = (value: string) =>
  checkField(value) && /^\d+$/.test(value.trim());

const isRowValid = (row: CertificateRow) =>
  checkField(row.email) &&
  isValidFullName(row.recipient_name) &&
  checkField(row.course_name) &&
  isValidInteger(row.duration_in_hours) &&
  isValidDate(row.completed_at);

const getRowErrors = (row: CertificateRow) => ({
  email: !checkField(row.email) ? 'Campo obrigatório' : undefined,
  recipient_name: getFullNameValidationError(row.recipient_name),
  course_name: !checkField(row.course_name) ? 'Campo obrigatório' : undefined,
  duration_in_hours: !isValidInteger(row.duration_in_hours)
    ? checkField(row.duration_in_hours)
      ? 'Duração deve ser um número inteiro'
      : 'Campo obrigatório'
    : undefined,
  completed_at: !isValidDate(row.completed_at) ? 'Data deve estar no formato DD/MM/AAAA' : undefined
});

const renderCell = (text: string, isStrikethrough: boolean, errorMsg?: string) => {
  const content = (
    <span
      style={{
        textDecoration: isStrikethrough ? 'line-through' : 'none',
        opacity: isStrikethrough ? 0.6 : 1,
        color: errorMsg ? '#ff4d4f' : undefined
      }}
    >
      {text || '—'}
    </span>
  );

  return errorMsg ? <Tooltip title={errorMsg}>{content}</Tooltip> : content;
};

export const BulkCertificateCreate = () => {
  const router = useNavigation();
  
  const { formProps, saveButtonProps, mutationResult } = useForm({
    redirect: false,
    resource: 'bulk_course_certificates',
    successNotification: () => ({
      type: 'success',
      description: 'Os certificados estão sendo gerados',
      message: 'Aguarde, eles estarão disponíveis em breve.'
    }),
    onMutationSuccess: () => {
      router.list('course_certificates');
    }
  });

  const { selectProps: certificateKeySelectProps } = useSelect<ICertificateKey>(
    {
      resource: 'certificate_keys',
      optionLabel: 'key',
      optionValue: 'key',

      pagination: {
        mode: 'server'
      }
    }
  );

  const [certificateList, setCertificateList] = useState<CertificateRow[]>([]);
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set());

  const activeCertificateList = certificateList.filter(
    (row, index) => !removedIndices.has(index) && isRowValid(row)
  );

  const hasInvalidRows = certificateList.some(
    (row, index) => !removedIndices.has(index) && !isRowValid(row)
  );

  const valid = activeCertificateList.length > 0;

  const handleRemove = (index: number) => {
    setRemovedIndices(prev => new Set(prev).add(index));
  };

  const handleRestore = (index: number) => {
    setRemovedIndices(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  const onFinish = (values: {}) => {
    formProps.onFinish!({ ...values, bulk: activeCertificateList });
  };

  const onFinishFailed: React.ComponentProps<typeof Form>['onFinishFailed'] = ({ errorFields }) => {
    const hasTemplateError = errorFields.some((field) => field.name[0] === 'template_key');

    if (hasTemplateError) {
      formProps.form?.scrollToField('template_key', { block: 'center' });
      setTimeout(() => {
        const templateInput =
          document.getElementById('template_key') ||
          document.querySelector('#template_key_list') ||
          document.querySelector('[aria-controls="template_key_list"]');

        if (templateInput instanceof HTMLElement) {
          templateInput.focus();
        }
      }, 0);
    }
  };

  return (
    <CreateLayout
      breadcrumb={
        <Breadcrumb>
          <Breadcrumb.Item>
            <Flex gap={4}>
              <AuditOutlined />
              Gestão de certificados
            </Flex>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/certificate_management/course_certificates">
              Certificados de Cursos Livres
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Adicionar certificados em lote</Breadcrumb.Item>
        </Breadcrumb>
      }
      saveButtonProps={{
        ...saveButtonProps,
        disabled: !valid || activeCertificateList.length === 0 || mutationResult.isLoading,
        loading: mutationResult.isLoading
      }}
    >
      <PageHeader
        title="Adicionar certificados em lote"
        icon={AuditOutlined}
      />

      <Card>
        <Alert
          type="info"
          showIcon
          message="Formato do arquivo CSV"
          description={
            <div>
              <p>
                O arquivo deve ser um CSV (valores separados por vírgula) com as seguintes colunas (nesta ordem):
              </p>
              <ul style={{ marginLeft: 20, marginTop: 8, marginBottom: 8 }}>
                <li>
                  <strong>Email</strong> - E-mail do aluno
                </li>
                <li>
                  <strong>Nome Completo</strong> - Nome completo do aluno
                </li>
                <li>
                  <strong>Curso</strong> - Nome do curso
                </li>
                <li>
                  <strong>Duração (horas)</strong> - Carga horária em horas
                </li>
                <li>
                  <strong>Data de conclusão</strong> - Formato: DD/MM/AAAA
                </li>
              </ul>
              <p style={{ marginTop: 8 }}>
                <strong>Exemplo:</strong>
                <br />
                <code style={{ fontSize: 12 }}>
                  Email,Nome Completo,Curso,Duração (horas),Data de conclusão
                  <br />
                  aluno@exemplo.com,João Silva,Introdução ao
                  Python,40,15/01/2024
                </code>
              </p>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>Observação:</strong> Caso não seja encontrado um usuário com o e-mail informado, o registro será ignorado durante o processamento.
              </p>
            </div>
          }
          style={{ marginBottom: 16 }}
        />

        <Form
          {...formProps}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          scrollToFirstError
        >
          <Form.Item>
            <Upload
              accept="text/csv"
              fileList={[]}
              onRemove={() => {
                setCertificateList([]);
                setRemovedIndices(new Set());
              }}
              beforeUpload={async newFile => {
                const rows = await parseFile(newFile);

                setCertificateList(rows);
                setRemovedIndices(new Set());

                return false;
              }}
            >
              <Button icon={<Icons.UploadOutlined />}>Escolher arquivo</Button>
            </Upload>
          </Form.Item>

          {certificateList.length > 0 && (
            <Form.Item
              label="Template"
              name="template_key"
              rules={[
                {
                  required: true,
                  message: 'Por favor, selecione um template'
                }
              ]}
            >
              <Select 
                {...certificateKeySelectProps} 
                placeholder="Selecione o template para os certificados"
              />
            </Form.Item>
          )}
        </Form>
        <FileTable 
          certificateList={certificateList} 
          removedIndices={removedIndices}
          onRemove={handleRemove}
          onRestore={handleRestore}
        />
        {hasInvalidRows && (
          <Alert
            type="warning"
            showIcon
            message="Linhas inválidas não serão importadas"
            description="Passe o mouse sobre os campos em vermelho para ver o motivo."
            style={{ marginTop: 12 }}
          />
        )}
      </Card>
    </CreateLayout>
  );
};

